import { invariant } from '@seen/core/invariant'
import { FacilityEncounter } from '@seen/core/models/encounters/facility-encounter'
import { ExtendedLocation } from '@seen/core/models/location'
import { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import type { JSX } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { CustomQuestionnaireForm } from '#src/components/questionnaire-form/CustomQuestionnaireForm.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useModalStore } from '#src/hooks/useModalStore.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export function CheckInDialog(): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const open = useModalStore((state) => state.checkInOpen)
  const setOpen = useModalStore((state) => state.setCheckInOpen)
  const participant = useModalStore((state) => state.participant)
  const setParticipant = useModalStore((state) => state.setParticipant)
  const appointment = useModalStore((state) => state.appointment)
  const setAppointment = useModalStore((state) => state.setAppointment)
  const currentPractitioner = useCurrentPractitioner()
  const transaction = useTransactionMutation({
    mutation: {
      onSuccess: () => {
        setOpen(false)
        setParticipant(null)
        setAppointment(null)
      },
    },
  })
  const { data: location } = useSearchOne(
    ExtendedLocation,
    ExtendedLocation.searchSGVLocation()
  )

  const onSubmit = (response: ExtendedQuestionnaireResponse): void => {
    transaction.mutate(async (bundle) => {
      bundle.create(response)
      invariant(participant, 'Participant is required')
      bundle.patch(participant, (participant) => {
        participant.isOnsite = true
      })
      if (appointment?.patient && location?.reference) {
        const encounter = new FacilityEncounter({
          patient: appointment.patient,
          practitioner: currentPractitioner?.reference,
          status: 'in-progress',
          location: location.reference,
        })
        encounter.start()
        bundle.create(encounter)
      }
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogDisplay>
        <DialogContent>
          <CustomQuestionnaireForm
            questionnaire={questionnaires.checkInQuestionnaire}
            onSubmit={(response) => {
              invariant(response, 'Response is required')
              onSubmit(new ExtendedQuestionnaireResponse(response))
            }}
            submitButtonText="Check In"
            hideSubmitButton={isReadOnly}
            prefillResponseAnswers_STABLE={
              participant
                ? [
                    {
                      linkId: 'participant',
                      answer: [{ valueReference: participant.reference }],
                    },
                  ]
                : []
            }
          />
        </DialogContent>
      </DialogDisplay>
    </Dialog>
  )
}
